// Animated canvas grid used as a hero background. Vanilla port of a React
// canvas component: config comes from data-* attributes, one instance per
// `[data-shape-grid]` canvas.
//
// The animation pauses when the canvas leaves the viewport and when the tab is
// hidden, and never starts at all under `prefers-reduced-motion` — the static
// `.grid-overlay` CSS stays as the fallback in that case.

const DEFAULTS = {
  direction: "diagonal",
  speed: 0.35,
  borderColor: "rgba(115, 235, 228, 0.045)",
  hoverFillColor: "rgba(115, 235, 228, 0.12)",
  squareSize: 50,
  hoverTrailAmount: 6,
}

function readConfig(canvas) {
  const d = canvas.dataset
  return {
    direction: d.direction || DEFAULTS.direction,
    speed: d.speed ? parseFloat(d.speed) : DEFAULTS.speed,
    borderColor: d.borderColor || DEFAULTS.borderColor,
    hoverFillColor: d.hoverFillColor || DEFAULTS.hoverFillColor,
    squareSize: d.squareSize ? parseInt(d.squareSize, 10) : DEFAULTS.squareSize,
    hoverTrailAmount: d.hoverTrailAmount ? parseInt(d.hoverTrailAmount, 10) : DEFAULTS.hoverTrailAmount,
  }
}

function initShapeGrid(canvas) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return

  const cfg = readConfig(canvas)
  const { squareSize } = cfg

  const offset = { x: 0, y: 0 }
  const cellOpacities = new Map()
  let hoveredCell = null
  let trailCells = []
  let rafId = null
  let width = 0
  let height = 0

  // Canvas pixels are sized to the device pixel ratio so strokes stay crisp on
  // retina displays, while the CSS box keeps its layout size.
  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    width = canvas.offsetWidth
    height = canvas.offsetHeight
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  const draw = () => {
    ctx.clearRect(0, 0, width, height)

    const offsetX = ((offset.x % squareSize) + squareSize) % squareSize
    const offsetY = ((offset.y % squareSize) + squareSize) % squareSize
    const cols = Math.ceil(width / squareSize) + 2
    const rows = Math.ceil(height / squareSize) + 2

    ctx.lineWidth = 1
    ctx.strokeStyle = cfg.borderColor

    for (let col = -1; col < cols; col++) {
      for (let row = -1; row < rows; row++) {
        const x = col * squareSize + offsetX
        const y = row * squareSize + offsetY

        const alpha = cellOpacities.get(`${col},${row}`)
        if (alpha) {
          ctx.globalAlpha = alpha
          ctx.fillStyle = cfg.hoverFillColor
          ctx.fillRect(x, y, squareSize, squareSize)
          ctx.globalAlpha = 1
        }

        ctx.strokeRect(x + 0.5, y + 0.5, squareSize, squareSize)
      }
    }
  }

  const updateCellOpacities = () => {
    const targets = new Map()

    if (hoveredCell) targets.set(`${hoveredCell.x},${hoveredCell.y}`, 1)

    trailCells.forEach((cell, i) => {
      const key = `${cell.x},${cell.y}`
      if (!targets.has(key)) {
        targets.set(key, (trailCells.length - i) / (trailCells.length + 1))
      }
    })

    targets.forEach((_, key) => {
      if (!cellOpacities.has(key)) cellOpacities.set(key, 0)
    })

    cellOpacities.forEach((opacity, key) => {
      const next = opacity + ((targets.get(key) || 0) - opacity) * 0.15
      if (next < 0.005) cellOpacities.delete(key)
      else cellOpacities.set(key, next)
    })
  }

  const tick = () => {
    const step = Math.max(cfg.speed, 0.05)
    switch (cfg.direction) {
      case "right":
        offset.x = (offset.x - step + squareSize) % squareSize
        break
      case "left":
        offset.x = (offset.x + step + squareSize) % squareSize
        break
      case "up":
        offset.y = (offset.y + step + squareSize) % squareSize
        break
      case "down":
        offset.y = (offset.y - step + squareSize) % squareSize
        break
      case "diagonal":
        offset.x = (offset.x - step + squareSize) % squareSize
        offset.y = (offset.y - step + squareSize) % squareSize
        break
    }

    updateCellOpacities()
    draw()
    rafId = requestAnimationFrame(tick)
  }

  const setHovered = (x, y) => {
    if (hoveredCell && hoveredCell.x === x && hoveredCell.y === y) return
    if (hoveredCell && cfg.hoverTrailAmount > 0) {
      trailCells.unshift({ ...hoveredCell })
      trailCells = trailCells.slice(0, cfg.hoverTrailAmount)
    }
    hoveredCell = { x, y }
  }

  const handleMouseMove = (event) => {
    const rect = canvas.getBoundingClientRect()
    const offsetX = ((offset.x % squareSize) + squareSize) % squareSize
    const offsetY = ((offset.y % squareSize) + squareSize) % squareSize
    setHovered(
      Math.floor((event.clientX - rect.left - offsetX) / squareSize),
      Math.floor((event.clientY - rect.top - offsetY) / squareSize)
    )
  }

  const handleMouseLeave = () => {
    if (hoveredCell && cfg.hoverTrailAmount > 0) {
      trailCells.unshift({ ...hoveredCell })
      trailCells = trailCells.slice(0, cfg.hoverTrailAmount)
    }
    hoveredCell = null
  }

  // The canvas itself is pointer-events:none so it never swallows hero clicks;
  // hover is tracked from the section that contains it.
  const hoverTarget = canvas.closest("section") || canvas.parentElement
  hoverTarget.addEventListener("mousemove", handleMouseMove)
  hoverTarget.addEventListener("mouseleave", handleMouseLeave)

  let inViewport = false
  let pageVisible = !document.hidden

  const start = () => {
    if (inViewport && pageVisible && rafId === null) rafId = requestAnimationFrame(tick)
  }
  const stop = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      inViewport = entry.isIntersecting
      inViewport ? start() : stop()
    },
    { threshold: 0 }
  )
  observer.observe(canvas)

  document.addEventListener("visibilitychange", () => {
    pageVisible = !document.hidden
    pageVisible ? start() : stop()
  })

  window.addEventListener("resize", () => {
    resize()
    draw()
  })

  resize()
  draw()
  start()
}

export default function setupShapeGrids() {
  const canvases = document.querySelectorAll("canvas[data-shape-grid]")
  if (!canvases.length) return

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
  if (!("IntersectionObserver" in window)) return

  canvases.forEach((canvas) => {
    canvas.dataset.shapeGridActive = "true"
    initShapeGrid(canvas)
  })
}
